import { Injectable } from '@angular/core';
import { IItemForCalculatingRatioOption } from '@modeso/aumico-lib-annualreport-fe';
import { DateLocalePipe, EMPTY_ARRAY_LENGTH, NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IDisplayOptions,
    IFinStatements,
    IPrintOptions,
    ISignatureOptions,
    IStyleOptions,
    ItemType,
    ReportType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { I18NextService } from 'angular-i18next';
import { FlisAccountsHelper } from './flisAccounts.helper';
import { FlisTypesHelper } from './flisTypes.helper';
import { PrintPreviewHelper } from './print-preview.helper';

const balanceIndex = 0;
const incomeIndex = 1;

const BLACK_TEXT_COLOR = '#38323C';
const BLUE_HIGHLIGHT_COLOR = '#ecf4fa';
const BLUE_TITLE_COLOR = '#2d6da1';
const WHITE_HIGHLIGHT_COLOR = '#FFFFFF';
@Injectable()
export class PrintPageHelper {
    constructor(
        private flisAccountsHelper: FlisAccountsHelper,
        private flisTypesHelper: FlisTypesHelper,
        private dateLocalPipe: DateLocalePipe,
        private readonly i18next: I18NextService,

    ) { }

    public getFliIdToAccountsForAnnualReport(finStatements: IFinStatements[]): Map<string, IAccount[]> {
        if (finStatements == null || finStatements.length === 0) {
            return new Map();
        }
        return this.flisAccountsHelper.getFliIdToAccountsForTemplates(
            finStatements[balanceIndex].templateAndAccountsMapping.template,
            finStatements[incomeIndex].templateAndAccountsMapping.template,
        );
    }

    public getFliIdToTypeForAnnualReport(finStatements: IFinStatements[]): Map<string, FinancialLineItemType> {
        if (finStatements == null || finStatements.length === 0) {
            return new Map();
        }

        return this.flisTypesHelper.getFliIdToTypeForTemplates(
            finStatements[balanceIndex].templateAndAccountsMapping.template,
            finStatements[incomeIndex].templateAndAccountsMapping.template,
        );
    }

    public isCashFlowPrintOptionsNotChanged(
        previousDisplayOptionsValue: IDisplayOptions,
        currentDisplayOptionsValue: IDisplayOptions,
    ): boolean {
        const isShowCashFlowNotChanged = previousDisplayOptionsValue.showCfs === currentDisplayOptionsValue.showCfs;
        return (
            this.isAdjustmentsOrReclassesPrintOptionsNotChanged(
                previousDisplayOptionsValue,
                currentDisplayOptionsValue,
            ) && isShowCashFlowNotChanged
        );
    }

    public isAdjustmentsOrReclassesPrintOptionsNotChanged(
        previousDisplayOptionsValue: IDisplayOptions,
        currentDisplayOptionsValue: IDisplayOptions,
    ): boolean {
        const isAdjustmentOptionNotChanged =
            previousDisplayOptionsValue.showAdjustmentInSums === currentDisplayOptionsValue.showAdjustmentInSums;
        const isReclassesOptionNotChanged =
            previousDisplayOptionsValue.showReClassInSums === currentDisplayOptionsValue.showReClassInSums;
        return isAdjustmentOptionNotChanged && isReclassesOptionNotChanged;
    }

    public getInitialValueOfPrintDisplayOptions(): IDisplayOptions {
        return {
            hideDecimalPlaces: false,
            dateTitle: '',
            hideEmptyLines: false,
            showFliTotal: false,
            showGroupHeaders: false,
            showGroupTotals: true,
            mainTitle: '',
            hidePreviousYear: false,
            printInGrayscale: false,
            showAccounts: false,
            showAccountProofForBalance: false,
            showAccountProofForIncome: false,
            showAdjustmentInSums: false,
            showFcp: false,
            showAop: false,
            showCfs: false,
            showCompanyInfos: false,
            showCompanyLogo: false,
            showCoverSheet: false,
            showCustomPageNumbers: false,
            showNotes: false,
            showReClassInSums: false,
            showRatios: true,
            showShareholderInformation: false,
            showTableOfContents: false,
            showNotesInDetailInToc: false,
            showUIDonCover: false,
            subtitle: '',
            disableBalanceSheetPageBreak: false,
            incomeItemNameForCalculatingRatio: null,
            showComplementaryReports: true,
            showEms: false,
            showFas: true,
            showAccountNumbers: false,
            aopHideDecimalPlacesValues: false,
            aopHideDecimalPlacesRatios: false,
            aopHideEmptyLines: false,
            notesHideDecimalPlacesValues: false,
            notesHideDecimalPlacesRatios: false,
            notesHideEmptyLines: false,
            finStatementHideDecimalPlacesValues: false,
            finStatementHideDecimalPlacesRatios: false,
            finStatementHideEmptyLines: false,
            finStatementRoundToThousands: false,
            notesRoundToThousands: false,
            aopRoundToThousands: false,
            nonBreakingHyphen: false,
            showReportCompanyLogo: false,
            applyGroupingMasking: true,
            internalLabel: null,
            repeatAppendixTitle: true,
        };
    }

    public getInitialValueOfPrintSignatureOptions(): ISignatureOptions {
        return {
            hasSignature: false,
            hideNames: false,
            isCollectiveSignature: false,
            placeSignatureAfterAop: false,
            place: '',
            signature1: '',
            signature2: '',
            date: '',
        };
    }

    public getInitialValueOfPrintStyleOptions(): IStyleOptions {
        return {
            textColor: BLACK_TEXT_COLOR,
            titleColor: BLUE_TITLE_COLOR,
            backgroundColor: BLUE_HIGHLIGHT_COLOR,
            tableHeaderColor: BLACK_TEXT_COLOR,
            tableFooterColor: BLACK_TEXT_COLOR,
            backgroundColorPY: WHITE_HIGHLIGHT_COLOR,
        };
    }

    public preparePrintOptionsAutofillData(
        printOptions: IPrintOptions,
        previousDateTitleDefaultValue: string | undefined,
        currentDateTitleDefaultValue: string | undefined,
        reportType: ReportType | undefined,
        autofillReportType: ReportType | undefined,
    ): IPrintOptions {
        return {
            ...printOptions,
            displayOptions: {
                ...printOptions.displayOptions,
                mainTitle: reportType === autofillReportType
                    ? printOptions.displayOptions.mainTitle
                    : this.i18next.t(PrintPreviewHelper.getPredefinedLocalizationKeyForReportMainTitle(reportType)) as string,
                dateTitle: previousDateTitleDefaultValue + '-' + currentDateTitleDefaultValue,
            },
            signatureOptions: {
                ...printOptions.signatureOptions,
                date: this.dateLocalPipe.transform(new Date()).toString(),
            },
        };
    }

    public getIncomeItemsOptionsForCalculatingRatio(
        incomeTemplateGroup: IAccountMappingTemplateGroup,
        showAdjustmentInSums: boolean,
        showReClassInSums: boolean,
        noPreviousData: boolean,
    ): IItemForCalculatingRatioOption[] {
        const incomeItemsOptionsForCalculatingRatio: IItemForCalculatingRatioOption[] = [];

        const isValuesOfItemValidForRatioCalculation = this.isValuesOfItemValidForRatioCalculation(
            incomeTemplateGroup,
            showAdjustmentInSums,
            showReClassInSums,
            noPreviousData,
        );
        if (isValuesOfItemValidForRatioCalculation) {
            //For Main Parent Group, add group to array of options and it's flis
            const incomeItemsOptionForCalculatingRatio: IItemForCalculatingRatioOption = {
                name: incomeTemplateGroup.name,
                caption: incomeTemplateGroup.title,
                calculatedClosingBalance: incomeTemplateGroup.calculatedClosingBalance ?? 0,
                calculatedOpeningBalance: incomeTemplateGroup.calculatedOpeningBalance ?? 0,
                openingAdjustment: incomeTemplateGroup.openingAdjustment ?? 0,
                closingAdjustment: incomeTemplateGroup.closingAdjustment ?? 0,
                openingReClass: incomeTemplateGroup.openingReClass ?? 0,
                closingReClass: incomeTemplateGroup.closingReClass ?? 0,
            };
            incomeItemsOptionsForCalculatingRatio.push(incomeItemsOptionForCalculatingRatio);
        }

        incomeTemplateGroup.groups.forEach((group: IAccountMappingTemplateGroup) => {
            // adding the group itself to the array of options
            if (
                this.isValuesOfItemValidForRatioCalculation(
                    group,
                    showAdjustmentInSums,
                    showReClassInSums,
                    noPreviousData,
                )
            ) {
                const incomeItemsOptionForCalculatingRatio: IItemForCalculatingRatioOption = {
                    name: group.name,
                    caption: group.title,
                    calculatedClosingBalance: group.calculatedClosingBalance ?? 0,
                    calculatedOpeningBalance: group.calculatedOpeningBalance ?? 0,
                    openingAdjustment: group.openingAdjustment ?? 0,
                    closingAdjustment: group.closingAdjustment ?? 0,
                    openingReClass: group.openingReClass ?? 0,
                    closingReClass: group.closingReClass ?? 0,
                };
                incomeItemsOptionsForCalculatingRatio.push(incomeItemsOptionForCalculatingRatio);
            }

            if (group.groups != null && group.groups.length > EMPTY_ARRAY_LENGTH) {
                const nestedRatioReferenceOptions = this.getIncomeItemsOptionsForCalculatingRatio(
                    group,
                    showAdjustmentInSums,
                    showReClassInSums,
                    noPreviousData,
                );
                incomeItemsOptionsForCalculatingRatio.push(...nestedRatioReferenceOptions);
            }

            const groupRatioReferenceOptions = this.getGroupValidItemsForCalculatingRatio(
                group.items,
                showAdjustmentInSums,
                showReClassInSums,
                noPreviousData,
            );
            incomeItemsOptionsForCalculatingRatio.push(...groupRatioReferenceOptions);
        });

        //For Main Parent Group, add direct flis
        if (incomeTemplateGroup.items.length !== EMPTY_ARRAY_LENGTH) {
            const groupRatioReferenceOptions = this.getGroupValidItemsForCalculatingRatio(
                incomeTemplateGroup.items,
                showAdjustmentInSums,
                showReClassInSums,
                noPreviousData,
            );
            incomeItemsOptionsForCalculatingRatio.push(...groupRatioReferenceOptions);
        }
        return incomeItemsOptionsForCalculatingRatio;
    }

    private getGroupValidItemsForCalculatingRatio(
        items: AccountMappingTemplateGroupItem[],
        showAdjustmentInSums: boolean,
        showReClassInSums: boolean,
        noPreviousData: boolean,
    ): IItemForCalculatingRatioOption[] {
        const incomeItemsOptionsForCalculatingRatio: IItemForCalculatingRatioOption[] = [];
        items.forEach((item) => {
            if (item.type === ItemType.EMPTY || item.type === ItemType.HEADER) {
                return;
            }
            const isItemValidForRatioCalculation = this.isValuesOfItemValidForRatioCalculation(
                item,
                showAdjustmentInSums,
                showReClassInSums,
                noPreviousData,
            );
            if (
                isItemValidForRatioCalculation &&
                ((item as IAccountMappingTemplateGroupItemFLI).type === ItemType.FLI ||
                    item.type === ItemType.SUB_TOTAL)
            ) {
                const incomeItemsOptionForCalculatingRatio: IItemForCalculatingRatioOption = {
                    name: item.name,
                    caption: item.caption,
                    calculatedClosingBalance: item.calculatedClosingBalance ?? 0,
                    calculatedOpeningBalance: item.calculatedOpeningBalance ?? 0,
                    openingAdjustment: item.openingAdjustment ?? 0,
                    closingAdjustment: item.closingAdjustment ?? 0,
                    openingReClass: item.openingReClass ?? 0,
                    closingReClass: item.closingReClass ?? 0,
                };
                incomeItemsOptionsForCalculatingRatio.push(incomeItemsOptionForCalculatingRatio);
            }
        });
        return incomeItemsOptionsForCalculatingRatio;
    }

    private isValuesOfItemValidForRatioCalculation(
        item:
            | IAccountMappingTemplateGroup
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingTemplateGroupItemSubtotal,
        showAdjustmentInSums: boolean,
        showReClassInSums: boolean,
        hasNoPreviousYear: boolean,
    ): boolean {
        //Always return true, because it doesn't matter if it has zeros, we display N/A in this case
        return true;
        const hasNoPreviousYearAndIncomeItemClosingValueNotEqualZero =
            hasNoPreviousYear && !this.isIncomeItemClosingValueEqualZero(item, showAdjustmentInSums, showReClassInSums);
        const IncomeItemOpeningAndClosingNotEqualZero =
            !this.isIncomeItemClosingValueEqualZero(item, showAdjustmentInSums, showReClassInSums) &&
            !this.isIncomeItemOpeningValueEqualZero(item, showAdjustmentInSums, showReClassInSums);
        return hasNoPreviousYearAndIncomeItemClosingValueNotEqualZero || IncomeItemOpeningAndClosingNotEqualZero;
    }

    private isIncomeItemClosingValueEqualZero(
        item:
            | IAccountMappingTemplateGroup
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingTemplateGroupItemSubtotal,
        showAdjustmentInSums: boolean,
        showReClassInSums: boolean,
    ): boolean {
        if (!showAdjustmentInSums && !showReClassInSums && item.calculatedClosingBalance != null) {
            return NumberUtils.isZero(item.calculatedClosingBalance);
        }

        let closingReclass: number | undefined;
        if (showReClassInSums && item.closingReClass != null && NumberUtils.isFinite(item.closingReClass)) {
            closingReclass = NumberUtils.roundNumber(item.closingReClass);
        }
        let closingAdjustment: number | undefined;
        if (showAdjustmentInSums && item.closingAdjustment != null && NumberUtils.isFinite(item.closingAdjustment)) {
            closingAdjustment = NumberUtils.roundNumber(item.closingAdjustment);
        }

        const closingValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [item.calculatedClosingBalance, closingReclass, closingAdjustment],
            0,
        );

        return NumberUtils.isZero(closingValue);
    }

    private isIncomeItemOpeningValueEqualZero(
        item:
            | IAccountMappingTemplateGroup
            | IAccountMappingTemplateGroupItemFLI
            | IAccountMappingTemplateGroupItemSubtotal,
        showAdjustmentInSums: boolean,
        showReClassInSums: boolean,
    ): boolean {
        if (!showAdjustmentInSums && !showReClassInSums && item.calculatedOpeningBalance != null) {
            return NumberUtils.isZero(item.calculatedOpeningBalance);
        }

        let openingReclasses: number | undefined;
        if (showReClassInSums && item.openingReClass != null && NumberUtils.isFinite(item.openingReClass)) {
            openingReclasses = NumberUtils.roundNumber(item.openingReClass);
        }

        let openingAdjustment: number | undefined;
        if (showAdjustmentInSums && item.openingAdjustment != null && NumberUtils.isFinite(item.openingAdjustment)) {
            openingAdjustment = NumberUtils.roundNumber(item.openingAdjustment);
        }

        const openingValue = NumberUtils.sumIgnoringInfiniteNumbers(
            [item.calculatedOpeningBalance, openingReclasses, openingAdjustment],
            0,
        );

        return NumberUtils.isZero(openingValue);
    }
}
